import {
  PRODUCT_DISCOUNT_TYPE,
  AWARD_TYPES,
  TRANSACTION_DISCOUNT_TYPE,
  ConfigureFormValues,
  ConfigureFormInitialValues,
  EarningMechanismInitialFormValues,
} from '@occc-app/routes/AwardCatalog/types';
import {
  Award_Per_Counting_Strategy,
  EarningMechanismFragment,
  Offer_Engagement_Type,
  OfferFragment,
} from '@occc/graphql';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import { centsToDollars } from '@occc-app/lib/currency';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { getExternalFulfillmentAwardType } from '@occc-app/offerHelpers/utils/getExternalFulfillmentAwardType';

import {
  AwardType,
  DirtyFieldsType,
  GetConfigureFormOperations,
  Operation,
} from '@occc-app/offerHelpers/types';
import { getOfferNameAndDescriptionUpdateOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionUpdateOperations';
import { getOfferCustomDataOperation } from '@occc-app/offerHelpers/operations/getOfferCustomDataOperation';
import { makeOfferUpdateOperation } from '@occc-app/offerHelpers/operations/makeOfferUpdateOperation';
import { getPromptAtPumpOperations } from '@occc-app/routes/CampaignComponents/PromptAtPump/utils';
import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import {
  FulfillmentChannel,
  getDefaultTimezone,
  getVoucherStackingInitialValues,
} from '@occc/feat-constraints';

import { RollbackStackingConfig } from '@occc-app/dataAccessLayer/chain/types';
import { LimitTimePeriod } from '@occc/feat-limits';
import { awardConfigureValidatorByAwardType, constraintByType } from './constants';
import { getLimitsOperations } from './helpers/getLimitsOperations';
import { getConstraintOperations } from './helpers/getConstraintOperations';
import { getEarningMechanismOperations } from './helpers/getEarningMechanismOperations';
import { getOfferStackableUpdateOperations } from './helpers/getOfferStackableUpdateOperations';

export const getIsSupportFulfillmentLimits = (awardType: AWARD_TYPES | undefined) => {
  return awardType !== AWARD_TYPES.SWEEPSTAKES;
};

export const getIsSupportPreAuthConstraint = (awardType: AWARD_TYPES | undefined) => {
  return awardType === AWARD_TYPES.FUEL_ROLLBACK;
};

export const getIsSupportPaymentConstraint = (awardType: AWARD_TYPES | undefined) => {
  return awardType === AWARD_TYPES.FUEL_ROLLBACK;
};

export const getIsSupportPromptAtPump = (awardType: AWARD_TYPES | undefined) => {
  return awardType === AWARD_TYPES.FUEL_ROLLBACK;
};

export const getIsSupportFulfillmentChannelConstraint = (awardType: AWARD_TYPES | undefined) => {
  return (
    !!awardType &&
    [AWARD_TYPES.TRANSACTION, AWARD_TYPES.PRODUCT, AWARD_TYPES.FUEL_ROLLBACK].includes(awardType)
  );
};

export const getIsTransactionFrequencyLimitSupportedForAward = (
  awardType: AWARD_TYPES | undefined,
): boolean => {
  return awardType === AWARD_TYPES.FUEL_ROLLBACK;
};

export const getIsAwardTypeChanged = (award: OfferFragment, values: ConfigureFormValues) => {
  const requiresVoucherConstraint = award.constraints.find(constraintHelper.isRequiresVoucher);
  const requiresVoucherForDiscountConstraint = award.constraints.find(
    constraintHelper.isRequiresVoucherForDiscount,
  );
  const requiresTransactionVoucherConstraint = award.constraints.find(
    constraintHelper.isRequiresTransactionVoucher,
  );

  const constraintType =
    requiresVoucherConstraint?.__typename ||
    requiresVoucherForDiscountConstraint?.__typename ||
    requiresTransactionVoucherConstraint?.__typename;

  const newConstraintType = values.awardType ? constraintByType[values.awardType] : undefined;

  return constraintType !== newConstraintType;
};

export const getDefaultFormValues = (): ConfigureFormInitialValues => {
  return {
    transactionDiscount: {
      type: TRANSACTION_DISCOUNT_TYPE.PERCENT,
    },
    productDiscount: {
      discountType: Award_Per_Counting_Strategy.TotalPurchase,
      type: PRODUCT_DISCOUNT_TYPE.DISCOUNTED,
      productGroups: [],
      partialDiscount: false,
      allowCarryOver: false,
    },
    externalFulfillment: {
      awardType: AwardType.AwardNullable,
      awardVouchers: {
        isEnabled: false,
        voucherCount: 1,
      },
    },
  };
};

export const mapEarningMechanismToConfigureFormValues = (
  earningMechanism: EarningMechanismFragment | null | undefined,
  constraints: OfferFragment['constraints'],
): EarningMechanismInitialFormValues => {
  if (!earningMechanism) {
    return {
      awardType: AWARD_TYPES.SWEEPSTAKES,
    };
  }

  if (earningMechanismHelper.isTransactionDiscount(earningMechanism)) {
    const award = earningMechanism.transactionDiscountAward;

    const type = awardHelper.isTransactionPercentDiscountAward(award)
      ? TRANSACTION_DISCOUNT_TYPE.PERCENT
      : TRANSACTION_DISCOUNT_TYPE.DOLLARS;

    const amount = awardHelper.isTransactionPercentDiscountAward(award)
      ? award.percentDiscount
      : centsToDollars(award.moneyDiscount);

    const productGroups = earningMechanism.ineligibleProductGroups.map((productGroup) => {
      return { id: productGroup.id, name: productGroup.name };
    });

    return {
      awardType: AWARD_TYPES.TRANSACTION,
      transactionDiscount: {
        type: type,
        amount,
        productGroups,
        purchaseAmountThreshold: centsToDollars(earningMechanism.purchaseAmountThreshold),
      },
    };
  }

  const constraintForProductDiscount = constraintByType[AWARD_TYPES.PRODUCT];
  const constraintForFuelRollback = constraintByType[AWARD_TYPES.FUEL_ROLLBACK];

  const hasConstraintForProductDiscount = constraints.some(
    (constraint) => constraint.__typename === constraintForProductDiscount,
  );
  const hasConstraintForFuelRollback = constraints.some(
    (constraint) => constraint.__typename === constraintForFuelRollback,
  );

  if (
    earningMechanismHelper.isProductDiscount(earningMechanism) &&
    hasConstraintForProductDiscount
  ) {
    const award = earningMechanism.productDiscountAward;

    const isPriceOverride = awardHelper.isPriceOverrideAward(award) && award.maximumCost > 0;
    const productDiscountType = isPriceOverride
      ? PRODUCT_DISCOUNT_TYPE.OVERRIDE
      : PRODUCT_DISCOUNT_TYPE.FREE;
    const type = awardHelper.isMoneyDiscountAward(award)
      ? PRODUCT_DISCOUNT_TYPE.DISCOUNTED
      : productDiscountType;
    const maximumCost = awardHelper.isPriceOverrideAward(award) ? award.maximumCost : undefined;
    const amount = awardHelper.isMoneyDiscountAward(award) ? award.moneyAmount : maximumCost;

    const partialDiscount = awardHelper.isMoneyDiscountAward(award) ? award.partialDiscount : false;

    const discountType = awardHelper.isMoneyDiscountAward(award)
      ? award.awardPer
      : Award_Per_Counting_Strategy.IndividualPurchase;

    const productGroups = earningMechanism.eligibleProductGroups.map((productGroup) => {
      return { id: productGroup.id, name: productGroup.name };
    });

    return {
      awardType: AWARD_TYPES.PRODUCT,
      productDiscount: {
        type: type,
        amount: amount ? centsToDollars(amount).toString() : '',
        productGroups,
        partialDiscount,
        discountType,
        allowCarryOver: earningMechanism.allowCarryOver ?? false,
      },
    };
  }

  if (earningMechanismHelper.isProductDiscount(earningMechanism) && hasConstraintForFuelRollback) {
    const award = earningMechanism.productDiscountAward;
    const amount = awardHelper.isMoneyDiscountAward(award) ? award.moneyAmount : undefined;

    const productGroups = earningMechanism.eligibleProductGroups.map((productGroup) => {
      return { id: productGroup.id, name: productGroup.name };
    });

    return {
      awardType: AWARD_TYPES.FUEL_ROLLBACK,
      fuelRollback: {
        amount: amount ? centsToDollars(amount) : 0,
        productGroups,
      },
    };
  }

  if (earningMechanismHelper.isExternalFulfillment(earningMechanism)) {
    return {
      awardType: AWARD_TYPES.EXTERNAL_FULFILLMENT,
      externalFulfillment: {
        awardType: getExternalFulfillmentAwardType(earningMechanism),
        awardPoints:
          earningMechanism.externalFulfillmentAward &&
          awardHelper.isPointsAward(earningMechanism.externalFulfillmentAward)
            ? {
                points: earningMechanism.externalFulfillmentAward.points,
              }
            : undefined,
        awardVouchers:
          earningMechanism.externalFulfillmentAward &&
          awardHelper.isVouchersAward(earningMechanism.externalFulfillmentAward)
            ? {
                voucheredOffer: earningMechanism.externalFulfillmentAward.voucheredOffer,
                voucherCount: earningMechanism.externalFulfillmentAward.voucherCount,
                voucherExpirationDays:
                  earningMechanism.externalFulfillmentAward.voucherExpirationDays || undefined,
                isEnabled: !!earningMechanism.externalFulfillmentAward.voucherExpirationDays,
              }
            : undefined,
      },
    };
  }

  return {};
};

export const getIsStackableFieldDirty = (dirtyFields: DirtyFieldsType) => {
  return isFieldDirty<ConfigureFormValues>(dirtyFields, 'stackable');
};

export const getIsTransactionLimitSupported = (awardType: AWARD_TYPES | undefined) => {
  return awardType === AWARD_TYPES.FUEL_ROLLBACK;
};

export const getConfigureFormOperations: GetConfigureFormOperations<ConfigureFormValues> = (
  offerId: string,
  offer: OfferFragment | undefined,
  values: ConfigureFormValues,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  const ops: Operation[] = [];

  ops.push(...getOfferNameAndDescriptionUpdateOperations(offerId, values, dirtyFields));

  const customDataOp = getOfferCustomDataOperation({ dirtyFields, values, offerId });
  if (customDataOp) ops.push(customDataOp);

  ops.push(...getPromptAtPumpOperations(offer, values, dirtyFields));
  ops.push(...getOfferStackableUpdateOperations(offerId, offer, values, dirtyFields));
  ops.push(...getEarningMechanismOperations(offerId, offer, values, dirtyFields));
  ops.push(...getConstraintOperations(offerId, offer, values, dirtyFields));
  ops.push(...getLimitsOperations(offerId, offer, values, dirtyFields));

  const desiredEngagementType =
    values.awardType === AWARD_TYPES.SWEEPSTAKES
      ? Offer_Engagement_Type.SweepstakesEntry
      : Offer_Engagement_Type.Vouchered;

  ops.push(
    makeOfferUpdateOperation(offerId, {
      engagementType: desiredEngagementType,
    }),
  );

  return ops;
};

export const getTransformOfferToInitialValues = (args: {
  isFuelRollbackVoucherStackingEnabled: boolean;
  rollbackStackingConfig: RollbackStackingConfig;
}) => {
  const { isFuelRollbackVoucherStackingEnabled, rollbackStackingConfig } = args;

  return (): ConfigureFormInitialValues => ({
    ...getDefaultFormValues(),
    ...getOfferNameAndDescriptionInitialValues(undefined),

    awardCount: {
      isEnabled: false,
      membership: { isEnabled: false, timePeriod: LimitTimePeriod.Total },
      global: { isEnabled: false, timePeriod: LimitTimePeriod.Total },
      transactionFrequency: { isEnabled: false, rows: [] },
      transaction: { isEnabled: false },
    },

    fulfillmentWindow: { isEnabled: false, timezone: getDefaultTimezone() },
    voucherStackingConstraint: getVoucherStackingInitialValues(
      undefined,
      isFuelRollbackVoucherStackingEnabled,
    ),

    promptAtPump: { isEnabled: false, shortPrompt: '', longPrompt: '' },

    paymentMethodConstraint: { isEnabled: false },

    fulfillmentChannelConstraint: {
      isEnabled: false,
      channel: FulfillmentChannel.OrderAheadOnly,
    },

    stackable: rollbackStackingConfig === 'PER_OFFER' ? false : rollbackStackingConfig === 'ALL',
    displayForOrderAhead: false,
  });
};

export const validateConfigureForm = (value: ConfigureFormValues) => {
  return value.awardType && awardConfigureValidatorByAwardType[value.awardType](value);
};
